import React, { useState } from "react";
import { Box, ChevronDown } from "lucide-react";
import { EntireObjectIcon } from "../../ui/Icons";
import InfoTooltip from "../../ui/InfoTooltip";
import { SCENE_GRAPH_NODE_COLORS as NODE_COLORS } from "../../../utils/sceneGraphNodeAppearance";
import {
  LEGEND_HELP_TEXT,
  LEGEND_SWATCH_SIZE_PX,
  LEGEND_CURVE_VIEW_H_PX,
  LEGEND_SWATCH_CURVE_QUADRATIC_D,
  LEGEND_ITEMS,
  LEGEND_CURVE_ACTUATOR_EDGE,
} from "../constants";

const SceneGraphLegend = React.memo(function SceneGraphLegend({
  hiddenTypes,
  effectiveHiddenTypes,
  toggleType,
}) {
  const c = NODE_COLORS.body;
  /** Matches former tendon pill gray in the graph (see NODE_COLORS.tendon). */
  const objectSwatch = NODE_COLORS.tendon;
  const objectHidden = hiddenTypes.has("object");
  const bodyHidden = hiddenTypes.has("body");
  const [legendExpanded, setLegendExpanded] = useState(true);

  return (
    <div
      className="absolute right-3 z-20 inline-flex w-fit flex-col items-stretch gap-2 text-xs select-none"
      style={{ top: "0.75rem" }}
    >
      <div
        className="inline-flex w-fit flex-col items-start rounded-lg p-2.5"
        style={{
          backgroundColor: "rgba(18, 18, 20, 0.88)",
          backdropFilter: "blur(6px)",
        }}
      >
        <div className={`flex w-full min-w-0 items-center gap-1.5 ${legendExpanded ? "mb-2" : ""}`}>
          <button
            type="button"
            className="-ml-0.5 flex-shrink-0 rounded p-0.5 text-gray-300 transition-colors hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500/80"
            aria-expanded={legendExpanded}
            aria-controls={legendExpanded ? "scene-graph-legend-items" : undefined}
            aria-label={legendExpanded ? "Collapse legend" : "Expand legend"}
            onClick={(e) => {
              e.stopPropagation();
              setLegendExpanded((v) => !v);
            }}
          >
            <ChevronDown
              className={`h-4 w-4 transition-transform duration-200 ease-out ${legendExpanded ? "rotate-0" : "rotate-180"}`}
              aria-hidden
            />
          </button>
          <div className="text-gray-100 font-semibold text-sm">Legend</div>
          <InfoTooltip text={LEGEND_HELP_TEXT} widthClass="w-64" />
        </div>
        {legendExpanded ? (
          <div id="scene-graph-legend-items" className="grid grid-cols-1 gap-y-1.5">
            <div
              key="object"
              className="flex items-center gap-1.5"
              onClick={() => toggleType("object")}
              style={{
                opacity: objectHidden ? 0.38 : 1,
                cursor: "pointer",
                transition: "opacity 0.15s",
              }}
            >
              <div
                className="relative flex-shrink-0 flex items-center justify-center rounded-full"
                style={{
                  width: LEGEND_SWATCH_SIZE_PX,
                  height: LEGEND_SWATCH_SIZE_PX,
                  backgroundColor: objectHidden ? "#27272a" : objectSwatch.base,
                  border: `1.5px solid ${objectHidden ? "#52525b" : objectSwatch.stroke}`,
                  transition: "background-color 0.15s, border-color 0.15s",
                }}
              >
                <EntireObjectIcon className="w-3 h-3 text-white" />
                {objectHidden && (
                  <svg
                    viewBox={`0 0 ${LEGEND_SWATCH_SIZE_PX} ${LEGEND_SWATCH_SIZE_PX}`}
                    className="pointer-events-none absolute inset-0 h-full w-full"
                  >
                    <line
                      x1="4"
                      y1="16"
                      x2="16"
                      y2="4"
                      stroke="#a1a1aa"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
              </div>
              <span
                className="leading-none text-sm"
                style={{
                  color: objectHidden ? "#a1a1aa" : "#d1d5db",
                  transition: "color 0.15s",
                }}
              >
                Object
              </span>
            </div>
            <div
              key="body"
              className="flex items-center gap-1.5"
              onClick={() => toggleType("body")}
              style={{
                opacity: bodyHidden ? 0.38 : 1,
                cursor: "pointer",
                transition: "opacity 0.15s",
              }}
            >
              <div
                className="relative flex-shrink-0 flex items-center justify-center rounded-full"
                style={{
                  width: LEGEND_SWATCH_SIZE_PX,
                  height: LEGEND_SWATCH_SIZE_PX,
                  backgroundColor: bodyHidden ? "#27272a" : c.base,
                  border: `1.5px solid ${bodyHidden ? "#52525b" : c.stroke}`,
                  transition: "background-color 0.15s, border-color 0.15s",
                }}
              >
                <Box className="w-3 h-3 text-white" />
                {bodyHidden && (
                  <svg
                    viewBox={`0 0 ${LEGEND_SWATCH_SIZE_PX} ${LEGEND_SWATCH_SIZE_PX}`}
                    className="pointer-events-none absolute inset-0 h-full w-full"
                  >
                    <line
                      x1="4"
                      y1="16"
                      x2="16"
                      y2="4"
                      stroke="#a1a1aa"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
              </div>
              <span
                className="leading-none text-sm"
                style={{
                  color: bodyHidden ? "#a1a1aa" : "#d1d5db",
                  transition: "color 0.15s",
                }}
              >
                Body
              </span>
            </div>
            {LEGEND_ITEMS.map((item) => {
              const { type, label, Icon, iconCls, curve } = item;
              const color = NODE_COLORS[type];
              const userHidden = hiddenTypes.has(type);
              const locked = !userHidden && effectiveHiddenTypes.has(type);
              const hidden = userHidden || locked;
              const curveStroke = hidden ? "#52525b" : (curve?.stroke ?? color.stroke);
              const curveDash = curve?.dasharray;

              if (type === "actuator") {
                const actCurveStroke = hidden ? "#52525b" : LEGEND_CURVE_ACTUATOR_EDGE.stroke;
                const actCurveDash = LEGEND_CURVE_ACTUATOR_EDGE.dasharray;
                return (
                  <div
                    key={type}
                    className="flex items-center gap-1.5"
                    onClick={locked ? undefined : () => toggleType(type)}
                    style={{
                      opacity: hidden ? 0.38 : 1,
                      cursor: locked ? "default" : "pointer",
                      transition: "opacity 0.15s",
                    }}
                    title={locked ? "Hidden because Joint is off" : undefined}
                  >
                    <div
                      className="relative flex flex-shrink-0 items-center justify-center"
                      style={{
                        width: LEGEND_SWATCH_SIZE_PX,
                        height: LEGEND_SWATCH_SIZE_PX,
                      }}
                    >
                      <svg
                        width={LEGEND_SWATCH_SIZE_PX}
                        height={LEGEND_CURVE_VIEW_H_PX}
                        viewBox={`0 0 ${LEGEND_SWATCH_SIZE_PX} ${LEGEND_CURVE_VIEW_H_PX}`}
                        className="pointer-events-none absolute left-0 right-0 top-1/2 -translate-y-1/2 overflow-visible"
                        aria-hidden
                      >
                        <path
                          d={LEGEND_SWATCH_CURVE_QUADRATIC_D}
                          fill="none"
                          stroke={actCurveStroke}
                          strokeWidth={1.5}
                          strokeDasharray={actCurveDash}
                          strokeLinecap="round"
                          opacity={hidden ? 0.85 : 1}
                          style={{ transition: "stroke 0.15s, opacity 0.15s" }}
                        />
                      </svg>
                      <div
                        className="relative z-[1] flex items-center justify-center rounded-full"
                        style={{
                          width: 13,
                          height: 13,
                          backgroundColor: hidden ? "#27272a" : color.base,
                          border: `1.5px solid ${hidden ? "#52525b" : color.stroke}`,
                          transition: "background-color 0.15s, border-color 0.15s",
                        }}
                      >
                        <Icon className={`${iconCls} text-white`} />
                      </div>
                      {userHidden && (
                        <svg
                          viewBox={`0 0 ${LEGEND_SWATCH_SIZE_PX} ${LEGEND_SWATCH_SIZE_PX}`}
                          className="pointer-events-none absolute inset-0 h-full w-full"
                        >
                          <line
                            x1="4"
                            y1="16"
                            x2="16"
                            y2="4"
                            stroke="#a1a1aa"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      )}
                      {locked && (
                        <svg
                          viewBox={`0 0 ${LEGEND_SWATCH_SIZE_PX} ${LEGEND_SWATCH_SIZE_PX}`}
                          className="pointer-events-none absolute inset-0 h-full w-full"
                        >
                          <line
                            x1="4"
                            y1="16"
                            x2="16"
                            y2="4"
                            stroke="#6366f1"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeDasharray="2 2"
                          />
                        </svg>
                      )}
                    </div>
                    <span
                      className="leading-none text-sm"
                      style={{
                        color: hidden ? "#a1a1aa" : "#d1d5db",
                        transition: "color 0.15s",
                      }}
                    >
                      {label}
                    </span>
                  </div>
                );
              }

              return (
                <div
                  key={type}
                  className="flex items-center gap-1.5"
                  onClick={locked ? undefined : () => toggleType(type)}
                  style={{
                    opacity: hidden ? 0.38 : 1,
                    cursor: locked ? "default" : "pointer",
                    transition: "opacity 0.15s",
                  }}
                  title={locked ? "Hidden because Joint is off" : undefined}
                >
                  {curve ? (
                    <div
                      className="relative flex flex-shrink-0 items-center justify-center"
                      style={{
                        width: LEGEND_SWATCH_SIZE_PX,
                        height: LEGEND_CURVE_VIEW_H_PX,
                      }}
                    >
                      <svg
                        width={LEGEND_SWATCH_SIZE_PX}
                        height={LEGEND_CURVE_VIEW_H_PX}
                        viewBox={`0 0 ${LEGEND_SWATCH_SIZE_PX} ${LEGEND_CURVE_VIEW_H_PX}`}
                        className="overflow-visible"
                        aria-hidden
                      >
                        <path
                          d={LEGEND_SWATCH_CURVE_QUADRATIC_D}
                          fill="none"
                          stroke={curveStroke}
                          strokeWidth={1.5}
                          strokeDasharray={curveDash}
                          strokeLinecap="round"
                          opacity={hidden ? 0.85 : 1}
                          style={{ transition: "stroke 0.15s, opacity 0.15s" }}
                        />
                      </svg>
                      {userHidden && (
                        <svg
                          viewBox={`0 0 ${LEGEND_SWATCH_SIZE_PX} ${LEGEND_CURVE_VIEW_H_PX}`}
                          className="pointer-events-none absolute inset-0 h-full w-full"
                        >
                          <line
                            x1="3"
                            y1={LEGEND_CURVE_VIEW_H_PX - 1}
                            x2={LEGEND_SWATCH_SIZE_PX - 3}
                            y2="2"
                            stroke="#a1a1aa"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      )}
                      {locked && (
                        <svg
                          viewBox={`0 0 ${LEGEND_SWATCH_SIZE_PX} ${LEGEND_CURVE_VIEW_H_PX}`}
                          className="pointer-events-none absolute inset-0 h-full w-full"
                        >
                          <line
                            x1="3"
                            y1={LEGEND_CURVE_VIEW_H_PX - 1}
                            x2={LEGEND_SWATCH_SIZE_PX - 3}
                            y2="2"
                            stroke="#6366f1"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeDasharray="2 2"
                          />
                        </svg>
                      )}
                    </div>
                  ) : (
                    <div
                      className="relative flex flex-shrink-0 items-center justify-center rounded-full"
                      style={{
                        width: LEGEND_SWATCH_SIZE_PX,
                        height: LEGEND_SWATCH_SIZE_PX,
                        backgroundColor: hidden ? "#27272a" : color.base,
                        border: `1.5px solid ${hidden ? "#52525b" : color.stroke}`,
                        transition: "background-color 0.15s, border-color 0.15s",
                      }}
                    >
                      <Icon className={`${iconCls} text-white`} />
                      {userHidden && (
                        <svg
                          viewBox={`0 0 ${LEGEND_SWATCH_SIZE_PX} ${LEGEND_SWATCH_SIZE_PX}`}
                          className="pointer-events-none absolute inset-0 h-full w-full"
                        >
                          <line
                            x1="4"
                            y1="16"
                            x2="16"
                            y2="4"
                            stroke="#a1a1aa"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                          />
                        </svg>
                      )}
                      {locked && (
                        <svg
                          viewBox={`0 0 ${LEGEND_SWATCH_SIZE_PX} ${LEGEND_SWATCH_SIZE_PX}`}
                          className="pointer-events-none absolute inset-0 h-full w-full"
                        >
                          <line
                            x1="4"
                            y1="16"
                            x2="16"
                            y2="4"
                            stroke="#6366f1"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeDasharray="2 2"
                          />
                        </svg>
                      )}
                    </div>
                  )}
                  <span
                    className="leading-none text-sm"
                    style={{
                      color: hidden ? "#a1a1aa" : "#d1d5db",
                      transition: "color 0.15s",
                    }}
                  >
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
});

export default SceneGraphLegend;
